/**
 * Capacitor / mobile platform detection.
 *
 * 1. `IS_MOBILE_BUILD` — set at Vite build time when `BUILD_TARGET=mobile`
 *    (`import.meta.env.VITE_IS_MOBILE_BUILD === "1"`). Safe for render.
 *
 * 2. `isCapacitorNative()` — runtime `window.Capacitor`. Use in effects/handlers
 *    only; false during SSR (there is no SSR in the Vite app, but safe for any
 *    future prerender).
 */

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean
      getPlatform?: () => string
      Plugins?: {
        StatusBar?: {
          setBackgroundColor: (opts: { color: string }) => Promise<void>
          setStyle: (opts: { style: string }) => Promise<void>
          setOverlaysWebView: (opts: { overlay: boolean }) => Promise<void>
        }
      }
    }
  }
}

export const IS_MOBILE_BUILD: boolean = import.meta.env.VITE_IS_MOBILE_BUILD === "1"

export function isCapacitorNative(): boolean {
  if (typeof window === "undefined") return false
  const cap = window.Capacitor
  return typeof cap?.isNativePlatform === "function" && cap.isNativePlatform() === true
}

export type CapacitorPlatform = "ios" | "android" | "web"

export function getCapacitorPlatform(): CapacitorPlatform {
  if (typeof window === "undefined") return "web"
  const platform = window.Capacitor?.getPlatform?.()
  if (platform === "ios" || platform === "android") return platform
  return "web"
}

export function isCapacitorIOS(): boolean {
  return isCapacitorNative() && getCapacitorPlatform() === "ios"
}

export function isCapacitorAndroid(): boolean {
  return isCapacitorNative() && getCapacitorPlatform() === "android"
}
